// src/components/inventory/QuickActionsMenu.js
// Floating quick actions menu for inventory management
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • Actions do something: handleAction calls onAction(action, activeTab) (it only logged a
//     TODO). Actions the page cannot perform are hidden (`isSupported`) instead of dead.
//   • Menus for the new tabs: AI Intelligence, Fleet & Vehicles, Parts ↔ Assets, Smart Reorder,
//     and FlowSense actions on every tab (Ask FlowSense AI, Explain for the Ministry of Works).
//   • Keyboard hints were shown for Ctrl+N / Ctrl+S / Ctrl+C, which the browser keeps for
//     itself (new window, save page, copy) — hidden unless showShortcuts.
//   • Badge with the number of items to re-order (FlowSense) on the button.
// --- END UPDATE ---------------------------------------------------------------------------

import React, { useState, useMemo } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Icon,
  Text,
  useColorModeValue,
  Tooltip,
  Badge,
  HStack,
  Portal,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdShoppingCart,
  MdFileDownload,
  MdFileUpload,
  MdQrCodeScanner,
  MdPrint,
  MdNotifications,
  MdClose,
} from 'react-icons/md';
import { MdAutoAwesome, MdAccountBalance, MdRefresh, MdLocalShipping, MdLink, MdWarning } from 'react-icons/md'; // --- UPDATE
import { FiBox, FiTruck, FiTag } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);

// --- UPDATE: FlowSense actions on every tab
const AI_ACTIONS = [
  { icon: MdAutoAwesome, label: 'Ask FlowSense AI', action: 'askAi' },
  { icon: MdAccountBalance, label: 'Explain for the Ministry of Works', action: 'explainMinistry' },
];
// --- END UPDATE

const QuickActionsMenu = ({ activeTab, onAction = null, isSupported = null, badge = null, showShortcuts = false }) => { // --- UPDATE: + onAction, isSupported, badge, showShortcuts
  const [isOpen, setIsOpen] = useState(false);

  // Theme colors
  const menuBg = useColorModeValue('white', 'gray.700');
  const menuBorder = useColorModeValue('gray.200', 'gray.600');
  const fabBg = useColorModeValue('blue.500', 'blue.400');
  const fabHoverBg = useColorModeValue('blue.600', 'blue.500');
  const menuItemHoverBg = useColorModeValue('blue.50', 'blue.900');

  // ✅ FIX: Use useMemo instead of a function to define the array
  // This ensures the array is rebuilt only when activeTab changes
  const quickActions = useMemo(() => {
    switch (activeTab) {
      case 0: // Inventory Items
        return [
          {
            icon: FiBox,
            label: 'Add New Item',
            action: 'addItem',
            shortcut: 'Ctrl+N',
          },
          {
            icon: MdQrCodeScanner,
            label: 'Scan Barcode',
            action: 'scanBarcode',
            badge: 'New',
          },
          {
            icon: MdFileUpload,
            label: 'Import Items',
            action: 'importItems',
          },
          {
            icon: MdFileDownload,
            label: 'Export Inventory',
            action: 'exportInventory',
          },
          {
            icon: MdShoppingCart,
            label: 'Create Purchase Order',
            action: 'createPO',
          },
          {
            icon: MdPrint,
            label: 'Print Labels',
            action: 'printLabels',
          },
        ];

      case 1: // Suppliers
        return [
          {
            icon: FiTruck,
            label: 'Add Supplier',
            action: 'addSupplier',
            shortcut: 'Ctrl+S',
          },
          {
            icon: MdFileDownload,
            label: 'Export Suppliers',
            action: 'exportSuppliers',
          },
          {
            icon: MdFileUpload,
            label: 'Import Suppliers',
            action: 'importSuppliers',
          },
        ];

      case 2: // Categories
        return [
          {
            icon: FiTag,
            label: 'Add Category',
            action: 'addCategory',
            shortcut: 'Ctrl+C',
          },
          {
            icon: MdFileDownload,
            label: 'Export Categories',
            action: 'exportCategories',
          },
        ];

      case 3: // Audit History
        return [
          {
            icon: MdFileDownload,
            label: 'Export Audit Log',
            action: 'exportAudit',
          },
          {
            icon: MdPrint,
            label: 'Print Report',
            action: 'printReport',
          },
          {
            icon: MdNotifications,
            label: 'Set Alerts',
            action: 'setAlerts',
          },
        ];

      // --- UPDATE (MOW+ v2): new tabs
      case 4: // AI Intelligence
        return [
          { icon: MdRefresh, label: 'Re-run FlowSense analysis', action: 'refreshAi' },
          { icon: MdShoppingCart, label: 'Smart Reorder / draft PO', action: 'createPO' },
          { icon: MdFileDownload, label: 'Export re-order list', action: 'exportReorder' },
          { icon: MdFileDownload, label: 'Export items with AI columns', action: 'exportInventory' },
        ];
      case 5: // Fleet & Vehicles
        return [
          { icon: MdLocalShipping, label: 'Register vehicle', action: 'registerVehicle' },
          { icon: MdWarning, label: 'Vehicles with alerts', action: 'fleetAlerts' },
          { icon: MdFileDownload, label: 'Export fleet', action: 'exportFleet' },
        ];
      case 6: // Parts ↔ Assets
        return [
          { icon: MdLink, label: 'Link a part', action: 'linkPart' },
          { icon: MdFileDownload, label: 'Export items with links', action: 'exportInventory' },
        ];
      case 7: // Smart Reorder
        return [
          { icon: MdRefresh, label: 'Refresh suggestions', action: 'refreshReorder' },
          { icon: MdFileDownload, label: 'Export re-order list', action: 'exportReorder' },
        ];
      // --- UPDATE (Procurement MOW+ v1)
      case 8: // Purchase Orders
        return [
          { icon: MdShoppingCart, label: 'Raise new PO', action: 'raisePO' },
          { icon: MdFileDownload, label: 'Export purchase orders', action: 'exportPOs' },
          { icon: MdRefresh, label: 'Refresh purchasing AI', action: 'refreshPOs' },
        ];
      // --- END UPDATE
      // --- END UPDATE

      default:
        return [];
    }
  }, [activeTab]);

  // --- UPDATE: only what the page can do + FlowSense actions
  const actions = useMemo(() => {
    const list = [...quickActions, ...AI_ACTIONS];
    return isSupported ? list.filter((a) => isSupported(a.action, activeTab)) : list;
  }, [quickActions, isSupported, activeTab]);
  // --- END UPDATE

  const handleAction = (action) => {
    // --- UPDATE: perform the action (was: console.log + TODO)
    if (onAction) onAction(action, activeTab);
    else console.log('Quick action triggered:', action);
    // --- END UPDATE
    setIsOpen(false);
  };

  return (
    <Portal>
      <AnimatePresence>
        {/* Floating Action Button */}
        <MotionBox
          position="fixed"
          bottom={{ base: '20px', md: '40px' }}
          right={{ base: '20px', md: '40px' }}
          zIndex={1000}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ duration: 0.3, type: 'spring' }}
        >
          <Menu isOpen={isOpen} onClose={() => setIsOpen(false)} placement="top-end">
            <Tooltip
              label="Quick Actions"
              hasArrow
              placement="left"
              isDisabled={isOpen}
            >
              <MenuButton
                as={IconButton}
                icon={<Icon as={isOpen ? MdClose : MdAdd} boxSize={6} />}
                isRound
                size="lg"
                bg={fabBg}
                color="white"
                _hover={{ bg: fabHoverBg, transform: 'scale(1.1)' }}
                _active={{ bg: fabHoverBg, transform: 'scale(0.95)' }}
                boxShadow="2xl"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Quick actions"
                transition="all 0.2s"
                w="64px"
                h="64px"
              />
            </Tooltip>
            {/* --- UPDATE: re-order badge */}
            {badge > 0 && !isOpen && (
              <Badge position="absolute" top="-2px" right="-2px" colorScheme="red" borderRadius="full" px={2} fontSize="xs" pointerEvents="none">{badge}</Badge>
            )}

            <MenuList
              bg={menuBg}
              borderColor={menuBorder}
              borderRadius="xl"
              boxShadow="2xl"
              py={2}
              minW="260px"
            >
              <Box px={3} py={2} mb={2}>
                <Text fontSize="xs" fontWeight="700" textTransform="uppercase" opacity={0.6}>
                  Quick Actions
                </Text>
              </Box>

              {actions.map((action, index) => ( /* --- UPDATE: supported + FlowSense actions */
                <React.Fragment key={action.action}>
                  {index > 0 && (index % 3 === 0 || action.action === 'askAi') && <MenuDivider />}
                  <MenuItem
                    icon={<Icon as={action.icon} boxSize={5} />}
                    onClick={() => handleAction(action.action)}
                    borderRadius="lg"
                    mx={2}
                    fontSize="sm"
                    fontWeight="500"
                    _hover={{
                      bg: menuItemHoverBg,
                    }}
                  >
                    <HStack justify="space-between" flex="1">
                      <Text>{action.label}</Text>
                      <HStack spacing={2}>
                        {action.badge && (
                          <Badge
                            colorScheme="green"
                            fontSize="10px"
                            borderRadius="full"
                          >
                            {action.badge}
                          </Badge>
                        )}
                        {action.shortcut && showShortcuts && ( /* --- UPDATE */
                          <Text
                            fontSize="xs"
                            color="gray.400"
                            fontFamily="mono"
                          >
                            {action.shortcut}
                          </Text>
                        )}
                      </HStack>
                    </HStack>
                  </MenuItem>
                </React.Fragment>
              ))}
            </MenuList>
          </Menu>
        </MotionBox>
      </AnimatePresence>
    </Portal>
  );
};

export default QuickActionsMenu;
